// src/components/Drive/AdminPanels/ExpandedBusDeliveryRow.jsx
import React from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { MapPin, Bus, Car, Clock, ArrowDown } from "lucide-react";

/* ── Helpers ── */

function fmtMs(ms) {
  if (!ms) return "–";
  try {
    const d = new Date(ms);
    return d.toLocaleDateString("fi-FI", {
      day: "numeric",
      month: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return "–"; }
}

function fmtDuration(seconds) {
  if (!seconds) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h} t ${m} min`;
  return `${m} min`;
}

function fmtDistance(meters) {
  if (!meters) return null;
  return meters >= 1000
    ? `${(meters / 1000).toFixed(1)} km`
    : `${meters} m`;
}

/* ── Leg type icon + label ── */
function LegTypeChip({ type, busCompany, exactName, shortName }) {
  const isBus = type === "bus-transport";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {isBus
        ? <Bus size={12} opacity={0.7} />
        : <Car size={12} opacity={0.7} />}
      <Chip
        label={
          isBus
            ? (shortName || exactName || busCompany || "Bussi")
            : "Auto"
        }
        size="small"
        variant="outlined"
        color={isBus ? "primary" : "default"}
        sx={{ fontSize: "0.65rem", height: 20 }}
      />
      {isBus && exactName && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
          {exactName}
        </Typography>
      )}
    </Box>
  );
}

/* ── Single journey leg card ── */
function LegCard({ leg, index }) {
  const isBus = leg.type === "bus-transport";

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "0.5px solid",
        borderColor: isBus ? "primary.light" : "divider",
        borderRadius: 1,
        p: 1.25,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {/* Type + meta */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
        <LegTypeChip
          type={leg.type}
          busCompany={leg.busCompany}
          exactName={leg.exactName}
          shortName={leg.shortName}
        />
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          {leg.duration && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <Clock size={10} opacity={0.5} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                {fmtDuration(leg.duration)}
              </Typography>
            </Box>
          )}
          {leg.distance && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
              {fmtDistance(leg.distance)}
            </Typography>
          )}
          {leg.price != null && (
            <Chip
              label={`${leg.price} €`}
              size="small"
              color="success"
              sx={{ fontSize: "0.65rem", height: 18 }}
            />
          )}
        </Box>
      </Box>

      {/* Route */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <MapPin size={11} opacity={0.5} style={{ marginTop: 2, flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ display: "block", lineHeight: 1.3 }}>
              {leg.origin || "–"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {fmtMs(leg.start)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ pl: 2.5 }}>
          <ArrowDown size={10} opacity={0.3} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <MapPin size={11} opacity={0.5} style={{ marginTop: 2, flexShrink: 0 }} color="currentColor" />
          <Box>
            <Typography variant="caption" fontWeight={600} sx={{ display: "block", lineHeight: 1.3 }}>
              {leg.destination || "–"}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              {fmtMs(leg.end)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ── Main export ── */
export function ExpandedBusDeliveryRow({ delivery }) {
  const bd = delivery.busTransportData;
  if (!bd) return null;

  const legs = Array.isArray(bd.legs) ? bd.legs : [];
  const totalDuration = fmtDuration(bd.duration);

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        mx: 0.5,
        mb: 1,
        bgcolor: "background.default",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      {/* ── Summary header ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
          <Chip
            label={bd.name || bd.optionType || "Bussikuljetus"}
            size="small"
            color="primary"
            sx={{ fontSize: "0.7rem", fontWeight: 700 }}
          />
          {totalDuration && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
              <Clock size={11} opacity={0.5} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                Kokonaiskesto {totalDuration}
              </Typography>
            </Box>
          )}
        </Box>
        {bd.price != null && (
          <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontSize: "0.8rem" }}>
            {bd.price} € yhteensä
          </Typography>
        )}
      </Box>

      {/* ── Departure / arrival times ── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em", display: "block" }}>
            Lähtö
          </Typography>
          <Typography variant="caption" fontWeight={600}>{fmtMs(bd.start)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em", display: "block" }}>
            Perillä
          </Typography>
          <Typography variant="caption" fontWeight={600}>{fmtMs(bd.end)}</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: "divider", opacity: 0.6 }} />

      {/* ── Legs ── */}
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700, display: "block", mb: 1 }}
        >
          Matkan osat ({legs.length})
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {legs.map((leg, i) => (
            <LegCard key={leg.id || i} leg={leg} index={i} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}